import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { confirmPasswordReset } from "@/lib/auth";

const MIN_PASSWORD_LENGTH = 12;

/** Password reset: /reset-password/:token — token captured once, then
 * scrubbed from the address bar (same handling as the invite page). */
export function ResetPassword() {
  const { token: tokenParam } = useParams();
  const tokenRef = useRef(tokenParam ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [dead, setDead] = useState(!tokenParam);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { applySession } = useAuth();

  useEffect(() => {
    window.history.replaceState({}, "", "/reset-password");
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH || password !== confirm) return;
    setError("");
    setSubmitting(true);
    try {
      const session = await confirmPasswordReset(tokenRef.current, password);
      setPassword("");
      setConfirm("");
      applySession(session);
      navigate("/", { replace: true });
    } catch (err: unknown) {
      // A dead token is the common failure; expose the resend path.
      setDead(true);
      setError(err instanceof Error ? err.message : "Something went wrong");
      setSubmitting(false);
    }
  }

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  return (
    <div className="canvas-ambient relative flex h-screen items-center justify-center bg-canvas">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>

      <div className="relative w-full max-w-sm overflow-hidden rounded-xl border border-line-subtle bg-surface p-8 shadow-[0_30px_60px_-30px_rgba(0,0,0,0.45)] space-y-6">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-electric-blue/60 to-transparent"
        />

        <div className="text-center space-y-2">
          <img src="/phantm-logo.png" alt="Phantm" className="mx-auto h-12 w-12 object-contain" />
          <h1 className="pt-1 text-xl font-semibold tracking-tight text-fg">
            Choose a new password
          </h1>
        </div>

        {dead ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-fg">
              This reset link is invalid or has expired.
            </p>
            <Link
              to="/forgot-password"
              className="text-sm text-electric-blue hover:underline"
            >
              Request a new link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md border border-block-red/30 bg-block-red/10 px-3 py-2 text-sm text-block-red">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-[10px] font-medium uppercase tracking-[0.14em] text-fg-faint"
              >
                New password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                autoFocus
                autoComplete="new-password"
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
              />
              {tooShort && (
                <p className="text-xs text-caution-amber">
                  Must be at least {MIN_PASSWORD_LENGTH} characters.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label
                htmlFor="confirm"
                className="text-[10px] font-medium uppercase tracking-[0.14em] text-fg-faint"
              >
                Confirm password
              </label>
              <input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repeat your password"
                autoComplete="new-password"
                className="w-full rounded-md border border-line bg-canvas px-3 py-2 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none focus:ring-2 focus:ring-electric-blue/40"
              />
              {mismatch && (
                <p className="text-xs text-caution-amber">Passwords don't match.</p>
              )}
            </div>
            <button
              type="submit"
              disabled={
                password.length < MIN_PASSWORD_LENGTH || password !== confirm || submitting
              }
              className="w-full rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-all hover:bg-electric-blue/90 focus:outline-none focus:ring-2 focus:ring-electric-blue/50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {submitting ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
